// 🔹 React Core
import React, { FC, useState } from 'react';

// 🔹 Animation Library
import { AnimatePresence, motion } from 'framer-motion';

// 🔹 Internal Components
import { ProgramDaySlot } from './ProgramDaySlot';
import CommandDialog from 'src/components/Modals/CommandDialog/CommandDialog';

// 🔹 Custom Hooks
import { useProgramStore } from './useProgramStore';

// 🔹 Constants
import { EXERCISE_TYPES } from './ExerciseLibrary';

interface ProgramWeekProps {
  store: ReturnType<typeof useProgramStore>;
  week: ReturnType<typeof useProgramStore>['weeks'][0];
  onOpenLibrary?: () => void;
}

const DAYS_OF_WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

export const ProgramWeek: FC<ProgramWeekProps> = ({ store, week, onOpenLibrary }) => {
  const { toggleWeek, deleteWeek, toggleDaySelection, formattedDays } = store;
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);

  const availableDays = DAYS_OF_WEEK.filter((d) => formattedDays.includes(d));

  return (
    <div className={`program-week ${week.isOpen ? 'open' : 'closed'}`}>
      <div
        className="week-header"
        onClick={() => toggleWeek(week._id)}
        style={{ cursor: 'pointer' }}
      >
        <div className="week-header-content">
          <h3>{week._id}</h3>

          <AnimatePresence>
            {week.isOpen && (
              <motion.div
                className="week-days-selector"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                onClick={(e) => e.stopPropagation()}
              >
                {availableDays.map((day) => (
                  <button
                    key={day}
                    className={`day-pill ${week.selectedDays.includes(day) ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDaySelection(week._id, day);
                    }}
                  >
                    {day}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="week-header-actions">
          <button
            className="toggle-btn"
            onClick={(e) => {
              e.stopPropagation();
              toggleWeek(week._id);
            }}
          >
            <motion.svg
              width={20}
              height={20}
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              animate={{ rotate: !week.isOpen ? 180 : 0 }}
            >
              <path
                d="M4.16732 12.5L10.0007 6.66667L15.834 12.5"
                stroke="white"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </motion.svg>
          </button>
          <button
            className="delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteConfirmVisible(true);
            }}
            disabled={week._id === 'All weeks' || store.weeks.length === 1}
          >
            <svg
              width={20}
              height={20}
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.832 5.83334L15.1095 15.9516C15.0796 16.3721 14.8914 16.7656 14.583 17.053C14.2745 17.3402 13.8686 17.5 13.447 17.5H6.55036C6.12881 17.5 5.72292 17.3402 5.41443 17.053C5.10594 16.7656 4.91779 16.3721 4.88786 15.9516L4.16536 5.83334M8.33203 9.16666V14.1666M11.6654 9.16666V14.1666M12.4987 5.83334V3.33334C12.4987 3.11233 12.4109 2.90036 12.2546 2.74408C12.0983 2.5878 11.8864 2.5 11.6654 2.5H8.33203C8.11101 2.5 7.89906 2.5878 7.74277 2.74408C7.58649 2.90036 7.49869 3.11233 7.49869 3.33334V5.83334M3.33203 5.83334H16.6654"
                stroke="#999999"
                strokeWidth="1.33333"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {week.isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="week-header-mobile">
              <div className="week-days-selector">
                {availableDays.map((day) => (
                  <button
                    key={day}
                    className={`day-pill ${week.selectedDays.includes(day) ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDaySelection(week._id, day);
                    }}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            <div
              id={week._id === 'Week 1' ? 'week-days-scroll-container' : undefined}
              className="week-days-scroll-container"
            >
              {[...week.selectedDays]
                .sort((a, b) => DAYS_OF_WEEK.indexOf(a) - DAYS_OF_WEEK.indexOf(b))
                .map((day) => {
                  const dayData = week.daysData[day] || {};
                  const exCount = Object.values(dayData).reduce(
                    (sum, arr) => sum + (arr || []).length,
                    0,
                  );

                  return (
                    <div key={day} className="day-column">
                      <div className="day-column-header">
                        <h4>{day}</h4>
                        <span>{exCount} ex</span>
                      </div>

                      {EXERCISE_TYPES.filter(
                        (t) => !['all', 'fitness', 'cardio']?.includes(t.value),
                      ).map((type) => (
                        <ProgramDaySlot
                          key={type.value}
                          store={store}
                          weekId={week._id}
                          dayStr={day}
                          slotType={type.label as any}
                          placedExercises={dayData[type.label] || []}
                          recommendedCount={Math.floor(10 / (EXERCISE_TYPES.length - 1))}
                          onOpenLibrary={onOpenLibrary}
                        />
                      ))}
                    </div>
                  );
                })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CommandDialog
        visible={deleteConfirmVisible}
        onClose={() => setDeleteConfirmVisible(false)}
        onConfirm={() => {
          deleteWeek(week._id);
          setDeleteConfirmVisible(false);
        }}
        type="warning"
        title="Delete Week"
        description={`Are you sure you want to delete ${week._id}?`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};
